import { userCoursePerformanceId, type Course, type UserCoursePerformance } from '@webapp2/shared/model'
import { techniqueCategoryTitlesById } from '@webapp2/shared/technique-categories'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { router, type DebriefRouteLocation } from '../router'
import { prebriefId, registeredStateId, state, type BuildDownloadStatus } from '../state'
import { assignmentsByCourseId, soonestDueIncompleteStatusByCourseId } from './assignment-service'
import { embeddedModuleByTechniqueId, type TechniqueDetails } from './build-service'
import { ensureModulesRegistered } from './launch-service'

export type CourseDownloadInfo = {
  downloadStatus: BuildDownloadStatus
  buildOverride: TechniqueDetails
  course: Course
}

export async function startCourse(course: Course) {
  await router.push({
    name: 'prebrief',
    params: {
      organizationId: state.organizationId,
      courseId: course.id
    }
  })
}

export function useCourseRoute() {
  const route = useRoute() as DebriefRouteLocation
  const run = computed(() => state.runResults[route.params.runId])
  const courseId = computed(() => route.params.courseId ?? run.value?.courseId)
  const course = computed(() => state.courses[courseId.value]!)
  const prebrief = computed(() => state.prebriefs[prebriefId(course.value?.techniqueId)]!)
  const playerTechnique = computed(() => state.playerTechniques[course.value?.techniqueId]!)
  const techniqueModuleBuildDetails = computed(() => techniqueDetailsByCourseId.value[courseId.value])
  const moduleBuild = computed(
    () => techniqueModuleBuildDetails.value?.link ?? techniqueModuleBuildDetails.value?.courseLink
  )
  const userCoursePerformance = computed(() => currentUserCoursePerformance(course.value.id))
  const assignments = computed(() => assignmentsByCourseId.value[course.value?.id])
  return {
    route,
    run,
    course,
    prebrief,
    moduleBuild,
    playerTechnique,
    techniqueModuleBuildDetails,
    userCoursePerformance,
    assignments
  }
}

export const coursesByName = computed(() => Object.values(state.courses).sort((a, b) => a.name.localeCompare(b.name)))

export const hostableCourses = computed(() =>
  coursesByName.value.filter(x => state.linkedModuleBuilds[x.linkedModuleBuildsId]?.linuxBuild)
)

export function currentUserCoursePerformance(courseId: string): UserCoursePerformance | undefined {
  const { organizationId, user } = state
  if (!organizationId || !user) {
    return undefined
  }
  return state.userCoursePerformances[userCoursePerformanceId(organizationId, courseId, user.id)]
}

export function bestTestScore(course: Course): number | undefined {
  const performance = currentUserCoursePerformance(course.id)
  return performance?.bestRun.isTest ? Math.round(performance.bestRun.score) : undefined
}

export function hasRecordedRuns(course: Course): boolean {
  return !!currentUserCoursePerformance(course.id)
}

function compareCourses(courseA: Course, courseB: Course): number {
  const statusA = soonestDueIncompleteStatusByCourseId.value[courseA.id]
  const statusB = soonestDueIncompleteStatusByCourseId.value[courseB.id]
  if (statusA && statusB) {
    return (
      state.assignments[statusA.assignmentId]!.dueDate.localeCompare(
        state.assignments[statusB.assignmentId]!.dueDate
      ) ||
      (statusA.bestScore ?? -Infinity) - (statusB.bestScore ?? -Infinity) ||
      courseA.name.localeCompare(courseB.name)
    )
  }
  if (statusA || statusB) {
    return statusA ? -1 : 1
  }
  return (
    (bestTestScore(courseA) ?? Infinity) - (bestTestScore(courseB) ?? Infinity) ||
    courseA.name.localeCompare(courseB.name)
  )
}

export const coursesInDisplayOrder = computed(() => Object.values(state.courses).sort(compareCourses))

const assignedCourses = computed(() =>
  coursesInDisplayOrder.value.filter(course => course.id in soonestDueIncompleteStatusByCourseId.value)
)

function courseCategories(course: Course): string[] {
  return state.playerTechniques[course.techniqueId]?.categories ?? []
}

function courseHasCategory(course: Course, categoryId: string): boolean {
  return courseCategories(course).includes(categoryId)
}

type CategoryFilter = { id: string; label: string }

const categoryFilters = computed<CategoryFilter[]>(() => {
  const courses = Object.values(state.courses)
  const categoryIds = new Set(courses.flatMap(course => courseCategories(course)))
  return [...categoryIds]
    .filter(categoryId => courses.some(course => !courseHasCategory(course, categoryId)))
    .flatMap(categoryId => {
      const label = techniqueCategoryTitlesById[categoryId]
      return label ? [{ id: categoryId, label }] : []
    })
    .sort((a, b) => a.label.localeCompare(b.label))
})

function coursesInCategory(categoryId: string) {
  return coursesInDisplayOrder.value.filter(course => courseHasCategory(course, categoryId))
}

export type CourseFilterOption = { value: string; label: string; testid: string; badge?: number }

const allFilterOption: CourseFilterOption = { value: 'all', label: 'All', testid: 'all' }

const assignedFilterOptions = computed<CourseFilterOption[]>(() => {
  const assignedCourseCount = Object.keys(soonestDueIncompleteStatusByCourseId.value).length
  return assignedCourseCount > 0
    ? [{ value: 'assigned', label: 'Assigned', testid: 'assigned', badge: assignedCourseCount }]
    : []
})

const categoryFilterOptions = computed<CourseFilterOption[]>(() =>
  categoryFilters.value.map(categoryFilter => ({
    value: categoryFilter.id,
    label: categoryFilter.label,
    testid: `category-${categoryFilter.id}`
  }))
)

export const courseFilterOptions = computed<CourseFilterOption[]>(() => [
  allFilterOption,
  ...assignedFilterOptions.value,
  ...categoryFilterOptions.value
])

export function filteredCourses(filter: string) {
  if (filter === 'assigned') {
    return assignedCourses.value
  }
  if (filter !== 'all') {
    return coursesInCategory(filter)
  }
  return coursesInDisplayOrder.value
}

const coursesByTechniqueId = computed(() =>
  Object.values(state.courses).reduce<{ [techniqueId: string]: Course }>((acc, course) => {
    acc[course.techniqueId.toLowerCase()] = course
    return acc
  }, {})
)

export function courseByTechniqueId(techniqueId: string): Course | undefined {
  return coursesByTechniqueId.value[techniqueId.toLowerCase()]
}

export const techniqueDetailsByCourseId = computed(() =>
  Object.values(state.courses).reduce<{ [courseId: string]: TechniqueDetails }>((acc, course) => {
    const courseLink = state.linkedModuleBuilds[course.linkedModuleBuildsId]
    const embeddedModule = embeddedModuleByTechniqueId(course.techniqueId)
    const techniquePath = embeddedModule?.techniquePath ?? courseLink?.techniquePath
    if (!techniquePath) {
      return acc
    }
    if (embeddedModule) {
      acc[course.id] = { techniquePath, embeddedModule, courseLink }
      return acc
    }
    const registered = state.registeredStates[registeredStateId(techniquePath)]
    const registeredLink = registered ? state.linkedModuleBuilds[registered.linkedModuleBuildsId] : undefined
    acc[course.id] = { techniquePath, link: registeredLink ?? courseLink, courseLink }
    return acc
  }, {})
)

export function registerModuleBuildForCourse(courseId: string) {
  const courseLink = techniqueDetailsByCourseId.value[courseId]?.courseLink
  if (courseLink) {
    ensureModulesRegistered([courseLink])
  }
}
